import React, { useState, useEffect } from 'react';

// Função para aplicar a máscara de telefone (celular brasileiro)
const applyPhoneMask = (value) => {
    // Remove tudo que não for dígito
    let cleanedValue = value.replace(/\D/g, '');

    // Limita a 11 dígitos para o formato (DD) 9 9999-9999
    if (cleanedValue.length > 11) {
        cleanedValue = cleanedValue.substring(0, 11);
    }

    let maskedValue = '';

    if (cleanedValue.length > 0) {
        maskedValue = `(${cleanedValue.substring(0, 2)}`;
    }
    if (cleanedValue.length >= 3) {
        maskedValue += `) ${cleanedValue.substring(2, 3)}`;
    }
    if (cleanedValue.length >= 4) {
        maskedValue += ` ${cleanedValue.substring(3, 7)}`;
    }
    if (cleanedValue.length >= 8) {
        maskedValue += `-${cleanedValue.substring(7, 11)}`;
    }

    return maskedValue;
};


const UserModal = ({ user, onClose, onSave, isEditingOwnProfile }) => {
    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        telefone: '',
        nivel: 'user', // Valor padrão para novos usuários
        senha: ''
    });

    const [errors, setErrors] = useState({
        senha: ''
    });

    useEffect(() => {
        if (user) {
            // Ao editar, preenche com os dados existentes do usuário
            setFormData({
                nome: user.nome || '',
                email: user.email || '',
                // Aplica a máscara ao carregar o telefone existente
                telefone: user.telefone ? applyPhoneMask(user.telefone) : '',
                nivel: user.nivel,
                senha: '' // Nunca preencha a senha no formulário por segurança
            });
        } else {
            // Ao criar um novo usuário, reseta o formulário
            setFormData({
                nome: '',
                email: '',
                telefone: '',
                nivel: 'user',
                senha: ''
            });
        }
        setErrors({ senha: '' }); // Limpa quaisquer erros ao abrir o modal
    }, [user]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Aplica a máscara se o campo for o telefone
        if (name === 'telefone') {
            const maskedValue = applyPhoneMask(value);
            setFormData(prev => ({
                ...prev,
                [name]: maskedValue
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }

        // Limpa o erro de senha quando o usuário começa a digitar
        if (name === 'senha' && errors.senha) {
            setErrors(prev => ({ ...prev, senha: '' }));
        }
    };

    const validateForm = () => {
        let isValid = true;
        const newErrors = { senha: '' }; // Começa com erros limpos

        // A senha é obrigatória apenas para novos usuários,
        // ou se o usuário atual estiver alterando a senha.
        if (!user && !formData.senha) { // Se for novo usuário e senha vazia
            newErrors.senha = 'A senha é obrigatória para novos usuários.';
            isValid = false;
        } else if (formData.senha && formData.senha.length < 6) { // Se a senha for digitada, valida o comprimento
            newErrors.senha = 'A senha deve ter pelo menos 6 caracteres.';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Antes de salvar, remove a máscara do telefone para enviar apenas os dígitos
        const phoneDigits = formData.telefone.replace(/\D/g, '');

        let dataToSave = { ...formData, telefone: phoneDigits };
        if (user && formData.senha === '') {
            delete dataToSave.senha; // Não envia a senha se estiver vazia (para manter a antiga)
        }

        if (validateForm()) {
            onSave(dataToSave); // Envia os dados validados
        }
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '8px',
                width: '100%',
                maxWidth: '500px'
            }}>
                <h2 style={{ fontSize: '1.5rem', marginBottom: '15px' }}>
                    {user ? (isEditingOwnProfile ? 'Editar Meu Perfil' : 'Editar Usuário') : 'Cadastrar Novo Usuário'}
                </h2>

                <form onSubmit={handleSubmit}>
                    <div style={formGroupStyle}>
                        <label style={labelStyle}>Nome:</label>
                        <input
                            type="text"
                            name="nome"
                            value={formData.nome}
                            onChange={handleChange}
                            required
                            style={inputStyle}
                        />
                    </div>

                    <div style={formGroupStyle}>
                        <label style={labelStyle}>Email:</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            // Desabilita o campo de email se estiver editando o próprio perfil.
                            disabled={isEditingOwnProfile}
                            style={{ ...inputStyle, backgroundColor: isEditingOwnProfile ? '#f0f0f0' : 'white' }}
                        />
                    </div>

                    <div style={formGroupStyle}>
                        <label style={labelStyle}>Telefone:</label>
                        <input
                            type="tel" // Use type="tel" para telefones
                            name="telefone"
                            value={formData.telefone}
                            onChange={handleChange}
                            style={inputStyle}
                            placeholder="(DD) 9 9999-9999" // Placeholder para orientar o usuário
                        />
                    </div>

                    {/* O campo de Nível deve ser visível e editável SOMENTE para administradores */}
                    {!isEditingOwnProfile && (
                        <div style={formGroupStyle}>
                            <label style={labelStyle}>Nível:</label>
                            <select
                                name="nivel"
                                value={formData.nivel}
                                onChange={handleChange}
                                required
                                style={inputStyle}
                            >
                                <option value="user">Usuário</option>
                                <option value="admin">Administrador</option>
                            </select>
                        </div>
                    )}

                    <div style={formGroupStyle}>
                        <label style={labelStyle}>
                            Senha
                            {user ? ' (Deixe em branco para manter a senha atual)' : ' *'}
                        </label>
                        <input
                            type="password"
                            name="senha"
                            value={formData.senha}
                            onChange={handleChange}
                            required={!user} // É obrigatório apenas se o 'user' for nulo (novo usuário)
                            minLength={6}
                            style={{ ...inputStyle, border: errors.senha ? '1px solid #e74c3c' : '1px solid #ddd' }}
                        />
                        {errors.senha && (
                            <p style={{ color: '#e74c3c', fontSize: '0.75rem', marginTop: '5px' }}>
                                {errors.senha}
                            </p>
                        )}
                    </div>

                    <div style={buttonGroupStyle}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={cancelButtonStyle}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            style={saveButtonStyle}
                        >
                            Salvar
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- ESTILOS COMPACTADOS PARA O MODAL (Consistentes com outros modais) ---
const formGroupStyle = {
    marginBottom: '12px',
};

const labelStyle = {
    display: 'block',
    marginBottom: '5px',
    fontWeight: '600',
    color: '#555',
    fontSize: '0.8rem', // Fonte menor para labels
};

const inputStyle = {
    width: '100%',
    padding: '8px 10px', // Padding menor
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '0.9rem', // Fonte menor para inputs
    boxSizing: 'border-box',
};

const buttonGroupStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '15px',
};

const baseButtonStyle = {
    padding: '8px 15px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.9rem', // Fonte menor para botões
};

const cancelButtonStyle = {
    ...baseButtonStyle,
    backgroundColor: '#e74c3c',
    color: 'white',
};

const saveButtonStyle = {
    ...baseButtonStyle,
    backgroundColor: '#2ecc71',
    color: 'white',
};

export default UserModal;